import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton
 * @property {HTMLElement} videoControls
 * @property {HTMLInputElement} progressInput - The progress bar input
 * @property {HTMLElement} progressFill - The progress bar fill element
 * @property {HTMLElement} progressThumb - The progress bar thumb element
 * @property {HTMLElement} timestamp
 * @property {HTMLElement} muteButton
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  isMuted = true;

  /** @type {number | null} */
  #progressInterval = null;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    // Only pause if the event didn't originate from this element (to avoid pausing ourselves when we start playing)
    document.addEventListener(
      ThemeEvents.mediaStartedPlaying,
      (event) => {
        if (event.target !== this) {
          this.pauseMedia();
        }
      },
      { signal },
    );
    document.addEventListener(
      DialogCloseEvent.eventName,
      (event) => {
        if (event.target instanceof Node && event.target.contains(this)) this.pauseMedia();
      },
      { signal, capture: true },
    );

    document.addEventListener(
      DialogOpenEvent.eventName,
      (event) => {
        if (!this.hasAttribute('autoplay')) return;
        if (event.target instanceof Node && event.target.contains(this)) this.playMedia();
      },
      { signal, capture: true },
    );

    // `zoom-dialog` broadcasts on window instead of dispatching on itself, so it needs its own
    // listener — the capture listener above never sees an event whose target IS window.
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    // When the video autoplays, sync the controls state
    if (this.hasAttribute('autoplay')) {
      this.#initAutoplayControls();
    }
  }

  /** Initializes controls state for autoplay videos that are already in the DOM. */
  #initAutoplayControls() {
    const video = this.querySelector('video');
    const iframe = this.querySelector('iframe[data-video-type]');

    if (video) {
      const onPlaying = () => {
        this.isPlaying = true;
        this.setAttribute('data-media-loaded', 'true');
        this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
        this.updatePlayPauseHint(this.isPlaying);
        this.isMuted = video.muted;
        this.updateMuteHint(this.isMuted);
        this.startProgressTracking();
      };

      // Video might already be playing or about to play
      if (!video.paused) {
        onPlaying();
      } else {
        video.addEventListener('playing', onPlaying, { once: true });
      }
    } else if (iframe) {
      // For YouTube/Vimeo iframes with autoplay
      this.isPlaying = true;
      this.setAttribute('data-media-loaded', 'true');
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#progressInterval) {
      clearInterval(this.#progressInterval);
    }
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      // New icon structure
      const playIcon = toggleMediaButton.querySelector('.icon__play');
      const pauseIcon = toggleMediaButton.querySelector('.icon__pause');
      const playText = toggleMediaButton.querySelector('.video-player__play-text');
      const pauseText = toggleMediaButton.querySelector('.video-player__pause-text');

      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
      if (playText) playText.classList.toggle('hidden', isPlaying);
      if (pauseText) pauseText.classList.toggle('hidden', !isPlaying);

      // Legacy icon structure fallback
      const legacyPlayIcon = toggleMediaButton.querySelector('.icon-play');
      if (legacyPlayIcon) legacyPlayIcon.classList.toggle('hidden', isPlaying);
      const legacyPauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (legacyPauseIcon) legacyPauseIcon.classList.toggle('hidden', !isPlaying);
    }

    // Show/hide video controls
    const videoControls = this.refs.videoControls;
    if (videoControls) {
      videoControls.classList.remove('hidden');
    }
  }

  /**
   * Updates the mute button visual state
   * @param {boolean} isMuted - Whether the video is currently muted
   */
  updateMuteHint(isMuted) {
    const muteButton = this.refs.muteButton;
    if (muteButton instanceof HTMLElement) {
      const unmuteIcon = muteButton.querySelector('.icon__unmute');
      const muteIcon = muteButton.querySelector('.icon__mute');
      const unmuteText = muteButton.querySelector('.video-player__unmute-text');
      const muteText = muteButton.querySelector('.video-player__mute-text');

      if (unmuteIcon) unmuteIcon.classList.toggle('hidden', !isMuted);
      if (muteIcon) muteIcon.classList.toggle('hidden', isMuted);
      if (unmuteText) unmuteText.classList.toggle('hidden', !isMuted);
      if (muteText) muteText.classList.toggle('hidden', isMuted);
    }
  }

  /**
   * Formats seconds to MM:SS
   * @param {number} seconds
   * @returns {string}
   */
  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /** Updates the progress bar. */
  updateProgress() {
    const video = this.querySelector('video');
    if (!video) return;

    const progress = (video.currentTime / video.duration) * 100 || 0;
    const { progressInput, progressFill, progressThumb, timestamp } = this.refs;

    if (progressInput) progressInput.value = String(progress);
    if (progressFill) progressFill.style.width = `${progress}%`;
    if (progressThumb) progressThumb.style.left = `${progress}%`;
    if (timestamp) timestamp.textContent = this.formatTime(video.currentTime);
  }

  /** Starts tracking video progress. */
  startProgressTracking() {
    if (this.#progressInterval) {
      clearInterval(this.#progressInterval);
    }
    this.#progressInterval = window.setInterval(() => {
      this.updateProgress();
    }, 100);
  }

  /** Stops tracking video progress. */
  stopProgressTracking() {
    if (this.#progressInterval) {
      clearInterval(this.#progressInterval);
      this.#progressInterval = null;
    }
  }

  /** Seeks the video to a specific position. */
  seekVideo() {
    const video = this.querySelector('video');
    const progressInput = this.refs.progressInput;

    if (!video || !progressInput) return;

    const seekTime = (parseFloat(progressInput.value) / 100) * video.duration;
    video.currentTime = seekTime;
    this.updateProgress();
  }

  /** Toggles mute state. */
  toggleMute() {
    const video = this.querySelector('video');
    if (!video) return;

    this.isMuted = !this.isMuted;
    video.muted = this.isMuted;
    this.updateMuteHint(this.isMuted);
  }

  /**
   * Stops event propagation to prevent slideshow navigation
   * @param {Event} event
   */
  stopPropagation = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  /** Shows the deferred media content. */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const inlineMedia = this.querySelector(':scope > video, :scope > iframe');
    const content =
      inlineMedia ?? this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    if (!inlineMedia) this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement) {
      // Initialize mute state from video
      this.isMuted = content.muted;
      this.updateMuteHint(this.isMuted);

      // Start progress tracking
      this.startProgressTracking();

      if (content.getAttribute('autoplay')) {
        // force autoplay for safari
        content.play().catch(() => {});
      }
    }
  }

  /** Toggle play/pause state of the media. */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    // Load content first if not already loaded
    if (!this.getAttribute('data-media-loaded')) {
      this.showDeferredMedia();
      return;
    }

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*',
      );
    } else {
      this.querySelector('video')
        ?.play()
        .catch(() => {});
      this.startProgressTracking();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /** Pauses the media. */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*',
      );
    } else {
      this.querySelector('video')?.pause();
      this.stopProgressTracking();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/** A product model. */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal },
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal },
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
